import { useEffect, useMemo, useState } from "react";
import { htmlToText } from "@/components/RichText";
import { Link } from "react-router-dom";
import { Loader2, MapPin, Sparkles, Filter, ArrowUpDown } from "lucide-react";
import { US_STATES } from "@/lib/states";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Gallery() {
  usePageMeta({
    title: "Winner Gallery | #BlackGirlJoyChallenge",
    description: "Meet the 2026 #BlackGirlJoyChallenge winners and the joy projects they are bringing to their communities.",
  });
  const [rows, setRows] = useState(null);
  const [state, setState] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/gallery?toolkit=bgjcpt").then((r) => setRows(r.data.winners)).catch(() => setRows([]));
  }, []); // runs once on mount; `api` is a module singleton

  const states = useMemo(() => {
    const present = new Set((rows || []).map((w) => w.state).filter(Boolean));
    return US_STATES.filter((s) => present.has(s));
  }, [rows]);

  const shown = useMemo(() => {
    if (!rows) return null;
    const list = state ? rows.filter((w) => w.state === state) : [...rows];
    const byName = (a, b) => (a.name || "zz").localeCompare(b.name || "zz");
    if (sort === "name") list.sort(byName);
    else if (sort === "state") list.sort((a, b) => (a.state || "zz").localeCompare(b.state || "zz") || byName(a, b));
    else if (sort === "oldest") list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return list;
  }, [rows, state, sort]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <section className="relative overflow-hidden bg-sbg-burgundy">
        <div className="absolute inset-0 joy-dotgrid opacity-20" />
        <div className="relative mx-auto max-w-6xl px-5 py-14 lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white">
            <Sparkles className="h-3.5 w-3.5" /> 2026 Winners
          </span>
          <h1 className="mt-5 font-display text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
            #BlackGirlJoyChallenge Winner Gallery
          </h1>
          <p className="mt-4 max-w-2xl text-base text-sbg-blush md:text-lg">
            100 Black girls across the South, each with a plan to spread joy in her community. Meet the winners who chose to share their pages.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl flex-1 px-5 py-12">
        {rows && rows.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="gallery-controls">
            <label className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-sbg-burgundy ring-1 ring-sbg-raspberry/15">
              <Filter className="h-4 w-4 text-sbg-raspberry" />
              <select data-testid="gallery-state-filter" value={state} onChange={(e) => setState(e.target.value)} className="bg-transparent outline-none">
                <option value="">All states</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-sbg-burgundy ring-1 ring-sbg-raspberry/15">
              <ArrowUpDown className="h-4 w-4 text-sbg-raspberry" />
              <select data-testid="gallery-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent outline-none">
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name A–Z</option>
                <option value="state">State A–Z</option>
              </select>
            </label>
            <span className="text-sm text-sbg-burgundy/60" data-testid="gallery-count">
              {shown.length} {shown.length === 1 ? "winner" : "winners"}{state ? ` in ${state}` : ""}
            </span>
            {state && (
              <button data-testid="gallery-clear-filter" onClick={() => setState("")} className="text-sm font-bold text-sbg-fuchsia hover:underline">Clear</button>
            )}
          </div>
        )}
        {rows === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" /></div>
        ) : rows.length === 0 ? (
          <div data-testid="gallery-empty" className="rounded-3xl bg-white p-12 text-center ring-1 ring-sbg-raspberry/10">
            <h2 className="font-display text-lg font-bold text-sbg-burgundy md:text-xl">Winners are being added</h2>
            <p className="mt-2 text-sm text-sbg-burgundy/70">Check back soon to meet the 2026 class.</p>
            <Link to="/bgjcpt" className="mt-6 inline-block rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Are you a winner? Make your page</Link>
          </div>
        ) : shown.length === 0 ? (
          <div data-testid="gallery-no-match" className="rounded-3xl bg-white p-12 text-center ring-1 ring-sbg-raspberry/10">
            <h2 className="font-display text-lg font-bold text-sbg-burgundy md:text-xl">No winners in {state} yet</h2>
            <button onClick={() => setState("")} className="mt-4 rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">Show all states</button>
          </div>
        ) : (
          <div data-testid="gallery-grid" className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((w, i) => <GalleryCard key={w.slug} w={w} index={i} />)}
          </div>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}

function GalleryCard({ w, index }) {
  const location = [w.city, w.state].filter(Boolean).join(", ");
  return (
    <Link
      to={`/bgjcpt/${w.slug}`}
      data-testid={`gallery-card-${w.slug}`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      className="animate-fade-up group overflow-hidden rounded-3xl bg-white ring-1 ring-sbg-raspberry/10 transition-transform hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(173,28,93,0.18)]"
    >
      {w.framed_url ? (
        <img src={w.framed_url} alt={w.name ? `${w.name}, #BlackGirlJoyChallenge winner` : "#BlackGirlJoyChallenge winner"} className="aspect-square w-full object-cover" loading="lazy" />
      ) : (
        <div className="flex aspect-square items-center justify-center bg-sbg-blush/50 text-sm text-sbg-burgundy/60">Photo coming soon</div>
      )}
      <div className="p-5">
        <h3 className="font-display text-lg font-black text-sbg-burgundy">
          {w.name || "A #BlackGirlJoy Winner"}
          {w.age && <span data-testid={`gallery-age-${w.slug}`} className="ml-2 align-middle rounded-full bg-sbg-blush px-2 py-0.5 font-sans text-xs font-bold text-sbg-raspberry">Age {w.age}</span>}
        </h3>
        {location && <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-sbg-raspberry"><MapPin className="h-3.5 w-3.5" /> {location}</p>}
        {w.project_title && <p className="mt-3 text-sm font-bold text-sbg-burgundy/90">{w.project_title}</p>}
        {w.project_description && <p className="mt-1 line-clamp-3 text-sm text-sbg-burgundy/70">{htmlToText(w.project_description)}</p>}
        <span className="mt-4 inline-block text-sm font-bold text-sbg-fuchsia transition-transform group-hover:translate-x-1">View page →</span>
      </div>
    </Link>
  );
}
